/**
 * EventPackagesPage - Browse and book multi-vendor event packages
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import {
  Package, Star, Users, Tag, Building, Utensils, Camera, Music,
  Palette, CheckCircle, ArrowRight, Loader2, Percent, Sparkles
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service type icons
const SERVICE_ICONS = {
  'event-venues': Building,
  'catering-services': Utensils,
  'photography-videography': Camera,
  'djs-bands': Music,
  'event-decoration': Palette
};

// Package card component
function PackageCard({ pkg, currency, onBook }) {
  const savings = pkg.savings || (pkg.total_provider_price - pkg.final_price);
  
  return (
    <Card className="hover:shadow-lg transition-shadow relative overflow-hidden">
      {pkg.is_featured && (
        <div className="absolute top-0 right-0 bg-gradient-to-l from-purple-600 to-pink-600 text-white px-4 py-1 text-sm">
          <Sparkles className="w-4 h-4 inline mr-1" />
          Featured
        </div>
      )}
      
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{pkg.name}</CardTitle>
        <p className="text-sm text-gray-500">{pkg.description}</p>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Included Services */}
        <div>
          <p className="text-sm font-medium text-gray-600 mb-2">Includes {pkg.provider_count} providers:</p>
          <div className="flex flex-wrap gap-2">
            {pkg.providers?.map((p, idx) => {
              const Icon = SERVICE_ICONS[p.service_type] || Package;
              return (
                <Badge key={idx} variant="outline" className="flex items-center gap-1">
                  <Icon className="w-3 h-3" />
                  {p.provider_name}
                </Badge>
              );
            })}
          </div>
        </div>

        {/* Event Types */}
        <div className="flex flex-wrap gap-1">
          {pkg.event_types?.map(type => (
            <Badge key={type} className="bg-purple-100 text-purple-700 capitalize">
              {type}
            </Badge>
          ))}
        </div>

        {/* Guest Range */}
        {(pkg.min_guests || pkg.max_guests) && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Users className="w-4 h-4" />
            {pkg.min_guests && pkg.max_guests
              ? `${pkg.min_guests} - ${pkg.max_guests} guests`
              : pkg.min_guests
              ? `Min ${pkg.min_guests} guests`
              : `Max ${pkg.max_guests} guests`}
          </div>
        )}

        {/* Pricing */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 line-through">
                {currency} {pkg.total_provider_price?.toLocaleString()}
              </p>
              <p className="text-2xl font-bold text-purple-600">
                {currency} {pkg.final_price?.toLocaleString()}
              </p>
            </div>
            <div className="text-right">
              <Badge className="bg-green-100 text-green-700">
                <Percent className="w-3 h-3 mr-1" />
                {pkg.discount_percentage}% OFF
              </Badge>
              <p className="text-sm text-green-600 mt-1">
                Save {currency} {savings.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <Button 
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
          onClick={() => onBook(pkg)}
        >
          Book Package
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
}

// Package booking modal
function PackageBookingModal({ open, onClose, pkg, currency, onSubmit }) {
  const [eventDate, setEventDate] = useState('');
  const [eventType, setEventType] = useState(pkg?.event_types?.[0] || '');
  const [guestCount, setGuestCount] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!eventDate || !guestCount || !venueAddress || !contactInfo.name || !contactInfo.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        package_id: pkg.id,
        event_date: eventDate,
        event_type: eventType,
        guest_count: parseInt(guestCount),
        venue_address: venueAddress,
        special_requests: specialRequests,
        contact: contactInfo
      });
    } finally {
      setLoading(false);
    }
  };

  if (!pkg) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Book {pkg.name}</DialogTitle>
          <DialogDescription>
            {pkg.provider_count} providers included | Save {currency} {pkg.savings?.toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Event Date *</label>
              <Input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Event Type *</label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pkg.event_types?.map(type => (
                    <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Expected Guests *</label>
            <Input
              type="number"
              value={guestCount}
              onChange={(e) => setGuestCount(e.target.value)}
              placeholder="Number of guests"
              min={pkg.min_guests || 1}
              max={pkg.max_guests}
            />
          </div>

          <div>
            <label className="text-sm font-medium">Venue Address *</label>
            <Textarea
              value={venueAddress}
              onChange={(e) => setVenueAddress(e.target.value)}
              placeholder="Full venue address"
              rows={2}
            />
          </div>

          <div>
            <label className="text-sm font-medium">Special Requests</label>
            <Textarea
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e.target.value)}
              placeholder="Any special requirements for the providers..."
              rows={2}
            />
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium">Your Contact Details</p>
            <Input
              placeholder="Full name *"
              value={contactInfo.name}
              onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
            />
            <Input
              placeholder="Phone number *"
              value={contactInfo.phone}
              onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
            />
            <Input
              type="email"
              placeholder="Email address"
              value={contactInfo.email}
              onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
            />
          </div>

          {/* Summary */}
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="font-medium">Package Total</span>
              <span className="text-xl font-bold text-purple-600">
                {currency} {pkg.final_price?.toLocaleString()}
              </span>
            </div>
            <p className="text-sm text-purple-600 mt-1">
              All {pkg.provider_count} providers will be booked for your event
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Confirm Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EventPackagesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { country } = useCountry();

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);

  // Filters
  const [eventType, setEventType] = useState(searchParams.get('event_type') || 'all');
  const [guestCount, setGuestCount] = useState(searchParams.get('guests') || '');

  const currencySymbol = country === 'UK' ? '£' : 'Rs.';

  useEffect(() => {
    fetchPackages();
  }, [country, eventType, guestCount]);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/packages?country=${country}`;
      if (eventType && eventType !== 'all') url += `&event_type=${eventType}`;
      if (guestCount) url += `&guest_count=${guestCount}`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setPackages(data.packages || []);
      }
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookPackage = (pkg) => {
    setSelectedPackage(pkg);
    setBookingModalOpen(true);
  };

  const handleSubmitBooking = async (bookingData) => {
    try {
      const response = await fetch(`${API_URL}/api/packages/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Package booked! ${data.provider_bookings_count} providers notified.`);
        setBookingModalOpen(false);
        navigate('/events');
      } else {
        throw new Error('Booking failed');
      }
    } catch (error) {
      toast.error('Failed to book package. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Package className="w-8 h-8" />
            Event Packages
          </h1>
          <p className="text-purple-100 mt-2">
            Save up to 20% by booking multiple providers together
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Event Type</label>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger>
                    <SelectValue placeholder="All events" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Events</SelectItem>
                    <SelectItem value="weddings">Weddings</SelectItem>
                    <SelectItem value="corporate">Corporate Events</SelectItem>
                    <SelectItem value="birthday">Birthday Parties</SelectItem>
                    <SelectItem value="kids_parties">Kids Parties</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Guest Count</label>
                <Input
                  type="number"
                  value={guestCount}
                  onChange={(e) => setGuestCount(e.target.value)}
                  placeholder="Expected guests"
                />
              </div>
              <Button onClick={fetchPackages} variant="outline">
                Search Packages
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Package Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : packages.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {packages.map(pkg => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                currency={currencySymbol}
                onBook={handleBookPackage}
              />
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No packages found</h3>
            <p className="text-gray-500">
              Try adjusting your filters or browse individual providers
            </p>
            <Button onClick={() => navigate('/events')} className="mt-4">
              Browse Providers
            </Button>
          </Card>
        )}

        {/* Custom Package CTA */}
        <Card className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="py-8 text-center">
            <h3 className="text-xl font-bold text-purple-800 mb-2">
              Need a custom package?
            </h3>
            <p className="text-purple-600 mb-4">
              Let us suggest the perfect combination of providers for your event
            </p>
            <Button
              onClick={() => navigate('/events/custom-package')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Build Custom Package
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Booking Modal */}
      <PackageBookingModal
        open={bookingModalOpen}
        onClose={() => setBookingModalOpen(false)}
        pkg={selectedPackage}
        currency={currencySymbol}
        onSubmit={handleSubmitBooking}
      />
    </div>
  );
}
